/**
 * Derives the speed-trading WebSocket base URLs from the configured HTTP API
 * base, so a single `VITE_WOLFSWAP_API_URL` override (e.g. a localhost backend)
 * points both the REST client and the price / trade-notification sockets at the
 * same host. `http(s)://host` becomes `ws(s)://host`.
 */

const HTTP_API_BASE =
    import.meta.env.VITE_WOLFSWAP_API_URL ?? 'https://api.wolfswap.app';

function toWsOrigin(httpBase: string): string {
    // strip a trailing slash, then swap the http(s) scheme for ws(s)
    const trimmed = httpBase.replace(/\/+$/, '');
    if (trimmed.startsWith('https://')) {
        return `wss://${trimmed.slice('https://'.length)}`;
    }
    if (trimmed.startsWith('http://')) {
        return `ws://${trimmed.slice('http://'.length)}`;
    }
    // already a ws(s) origin or bare host — assume secure
    if (trimmed.startsWith('ws://') || trimmed.startsWith('wss://')) {
        return trimmed;
    }
    return `wss://${trimmed}`;
}

const WS_ORIGIN = toWsOrigin(HTTP_API_BASE);

// the public live tape is served by factions (it holds no secrets and must not
// share a process with trade signing), so its socket derives from that base
const FACTIONS_API_BASE =
    import.meta.env.VITE_WOLFSWAP_FACTIONS_API_URL ?? `${HTTP_API_BASE}/factions`;
const FACTIONS_WS_ORIGIN = toWsOrigin(FACTIONS_API_BASE);

/** Base for the public price stream: `<origin>/speed/ws` → `/speed/ws/:networkId/:pair`. */
export const SPEED_PRICE_WS_BASE = `${WS_ORIGIN}/speed/ws`;

/**
 * Base for the authenticated trade-status stream: `<origin>/speed/trades/ws`
 * → `/speed/trades/ws/:networkId?token=<jwt>`.
 */
export const SPEED_TRADES_WS_BASE = `${WS_ORIGIN}/speed/trades/ws`;

/**
 * Base for the PUBLIC live-trades tape: `<factions origin>/speed/live/ws` →
 * `/factions/speed/live/ws/:networkId`. No token — the tape carries nothing that
 * is not already public on Hyperliquid, and a logged-out visitor is who it is
 * for. The server pings every 25 s, so the client keeps no timer of its own.
 */
export const SPEED_LIVE_WS_BASE = `${FACTIONS_WS_ORIGIN}/speed/live/ws`;
